import {
  serializeSourceLineRange,
  type SourceLineRange,
} from "@/lib/source-span";
import { mapInternalResolution, type PublicResolution } from "../resolution";
import {
  encodeRouteKeyV1,
  encodeRouteKeyV1FromUlid,
  encodeRouteKeyV1FromUuid,
  routeKeyV1ToUuid,
  ROUTE_KEY_V1_LENGTH,
} from "../route-key-v1";

export type KnowledgeBaseAddressTarget = {
  family: "knowledge-base";
  version: 1;
  viewingTeamSlug: string;
  kbSlug: string;
  routeKey: string;
};

export type KnowledgeBaseIdentity = {
  kbId: string;
};

export type KnowledgeBaseCanonical = {
  viewingTeamSlug: string;
  kbSlug: string;
  routeKey: string;
};

const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ULID_LIKE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;
const KB_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function encodeKbRouteKey(kbId: string): string {
  if (UUID_LIKE.test(kbId)) return encodeRouteKeyV1FromUuid(kbId);
  if (ULID_LIKE.test(kbId)) return encodeRouteKeyV1FromUlid(kbId);
  // Short non-canonical fixture ids (≤16 UTF-8 bytes) pack into the 128-bit
  // envelope so unit tests can still emit composite locators. Longer strings
  // (legacy pseudo-ULID seed ids with I/L/O/U) are not reversible in 128 bits
  // and must not be truncated — truncation collided distinct seed KBs and
  // decoded to a UUID the API does not own (dev KB 404s).
  const encoded = new TextEncoder().encode(kbId);
  if (encoded.length > 16) {
    throw new Error(
      "Knowledge Base route-key v1 requires a UUID or ULID identity (128-bit); " +
        "non-canonical ids longer than 16 UTF-8 bytes cannot be packed reversibly",
    );
  }
  const bytes = new Uint8Array(16);
  bytes.set(encoded);
  return encodeRouteKeyV1(bytes);
}

/**
 * Format the physical `$kbId` route segment as `{kb-slug}~{route-key}`
 * (LAT-1505 / ADR 0046). The slug is never Query or authorization identity.
 */
export function formatKnowledgeBaseLocator(input: {
  kbSlug: string;
  kbId: string;
}): string {
  if (!KB_SLUG.test(input.kbSlug)) {
    throw new Error("Knowledge Base locator requires a canonical lowercase slug");
  }
  return `${input.kbSlug}~${encodeKbRouteKey(input.kbId)}`;
}

const FILE_PATH_ALPHABET = /^[A-Za-z0-9._/-]+$/;

/**
 * LAT-2183 — the Files-pane file address: locator plus `?tab=files&path=`
 * (and optional `lines=S-E`). `path` is an address, `file` (elsewhere) is
 * identity; this builder never emits `file=`.
 */
export function formatKnowledgeBaseFileAddress(input: {
  kbSlug: string;
  kbId: string;
  path: string;
  lines?: SourceLineRange;
}): { locator: string; search: string } {
  const locator = formatKnowledgeBaseLocator({
    kbSlug: input.kbSlug,
    kbId: input.kbId,
  });
  if (!FILE_PATH_ALPHABET.test(input.path)) {
    throw new Error("Knowledge Base file address requires a canonical path");
  }
  const params = new URLSearchParams();
  params.set("tab", "files");
  params.set("path", input.path);
  if (input.lines) params.set("lines", serializeSourceLineRange(input.lines));
  return { locator, search: `?${params.toString()}` };
}

/**
 * `formatKnowledgeBaseLocator` for callers that read KBs they do not own, and
 * therefore cannot assume canonical identity: `null` means "this row has no
 * addressable locator" instead of a throw.
 *
 * Product surfaces keep the throwing producer — they render one team's KBs,
 * all minted as UUIDs by the app, so a refusal there is a real invariant break
 * worth surfacing loudly. The cross-tenant operator directory is the exception:
 * it renders every row in the database, including whatever legacy or imported
 * identity predates route-key v1, and it is the very tool you would use to find
 * such a row. There, one unaddressable KB must cost that row its link and
 * nothing more (it blanked the whole Super Admin page instead).
 */
export function tryFormatKnowledgeBaseLocator(input: {
  kbSlug: string;
  kbId: string;
}): string | null {
  try {
    return formatKnowledgeBaseLocator(input);
  } catch {
    return null;
  }
}

/**
 * TanStack Router params for the KB family under a viewing Team slug.
 * Physical `$kbId` noun is unchanged; the value is the composite locator.
 */
export function kbBrowserRouteParams(input: {
  viewingTeamSlug: string;
  kbSlug: string;
  kbId: string;
}): { teamId: string; kbId: string } {
  const target = buildKnowledgeBaseTarget(input);
  return {
    teamId: target.viewingTeamSlug,
    kbId: formatKnowledgeBaseLocator({
      kbSlug: target.kbSlug,
      kbId: input.kbId,
    }),
  };
}

/** Decode route-key bytes to immutable KB id (UUID text form). */
export function knowledgeBaseIdentityFromRouteKey(routeKey: string): string {
  return routeKeyV1ToUuid(routeKey);
}

/** Zero-I/O builder from an already-loaded KB projection. */
export function buildKnowledgeBaseTarget(input: {
  viewingTeamSlug: string;
  kbSlug: string;
  kbId: string;
}): KnowledgeBaseAddressTarget {
  return {
    family: "knowledge-base",
    version: 1,
    viewingTeamSlug: input.viewingTeamSlug,
    kbSlug: input.kbSlug,
    routeKey: encodeKbRouteKey(input.kbId),
  };
}

export function parseKnowledgeBaseLocator(
  segment: string,
):
  | { ok: true; kbSlug: string; routeKey: string }
  | { ok: false; reason: "malformed" } {
  const separator = segment.lastIndexOf("~");
  if (separator <= 0 || separator !== segment.indexOf("~")) {
    return { ok: false, reason: "malformed" };
  }
  const kbSlug = segment.slice(0, separator);
  const routeKey = segment.slice(separator + 1);
  if (!KB_SLUG.test(kbSlug) || routeKey.length !== ROUTE_KEY_V1_LENGTH) {
    return { ok: false, reason: "malformed" };
  }
  return { ok: true, kbSlug, routeKey };
}

/**
 * Local reversible resolution: the route key selects immutable identity.
 * A stale slug with the correct key history-replaces to the current slug.
 */
export function resolveKnowledgeBaseAddressLocally(input: {
  viewingTeamSlug: string;
  kbSlug: string;
  routeKey: string;
  currentKbSlug: string;
}): PublicResolution<
  KnowledgeBaseIdentity,
  KnowledgeBaseCanonical,
  KnowledgeBaseAddressTarget
> {
  let kbId: string;
  try {
    kbId = routeKeyV1ToUuid(input.routeKey);
  } catch {
    return mapInternalResolution({ status: "failure", reason: "malformed" });
  }

  const canonical: KnowledgeBaseCanonical = {
    viewingTeamSlug: input.viewingTeamSlug,
    kbSlug: input.currentKbSlug,
    routeKey: input.routeKey,
  };
  const target = buildKnowledgeBaseTarget({
    viewingTeamSlug: input.viewingTeamSlug,
    kbSlug: input.currentKbSlug,
    kbId,
  });

  if (input.kbSlug === input.currentKbSlug) {
    return mapInternalResolution({
      status: "resolved",
      identity: { kbId },
      canonical,
      disposition: { kind: "keep" },
    });
  }

  return mapInternalResolution({
    status: "resolved",
    identity: { kbId },
    canonical,
    disposition: { kind: "replace", target },
  });
}
